import { Fragment, useEffect, useState } from "react";
import { capitalize, first, startCase } from "lodash";
import { Avatar, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import BackIcon from "../../../../custom-components/buttons/BackIcon";
import { useGetStudent as useGetStudentApi } from "@services/shared/student";
import { useGetStudentParents } from "@services/student/parents";
import { useParams } from "react-router-dom";
import { formatDate } from "../../../../../utils/helper";
import AnalyticsTabs from "./AnalyticsTabs";
import RecentActivities from "./RecentActivities";
import PendingLessons from "./PendingLessons";
import { getStudentGradeLevelExtras } from "../../../../../services/teacher/students";
import PageHeader from "@components/shared/ui/PageHeader";
import SurfaceCard from "@components/shared/ui/SurfaceCard";

const StudentProfile = () => {
  const { student_id } = useParams();

  // Hook is `useGetStudent(student_id: string)` (single arg). Previous call
  // passed `({}, student_id)` and destructured `studentData` / `getStudentData`
  // — neither name exists on the hook's return, so `getStudentData()` resolved
  // to `undefined` and threw "n is not a function" on every mount.
  // See SENTRY-COPPER-ELEPHANT-R.
  const { studentInfo: data, getForm: getStudentData } = useGetStudentApi(student_id ?? "");
  // Parents are joined through the `student_parents` junction, not stored
  // as an array on the student row — the legacy array-shape reader
  // crashed in dev and rendered empty in prod (2026-05-13).
  const { rows: parents } = useGetStudentParents(student_id);

  // Profile fields lived on `user.details.*` which doesn't exist as a column.
  // Student fields live on `students` and grade/curriculum/school-year linkage
  // on `student_grade_levels`. Same root cause as bugs f7642133 + 68bb8bce
  // (2026-05-16, "crash when checking students" / "page leads to error"):
  // the page rendered fields that were undefined, which then chained into
  // null-deref in helper calls.
  const [extras, setExtras] = useState<{
    grade_level: string | null;
    curriculum_name: string | null;
    school_year_name: string | null;
  }>({ grade_level: null, curriculum_name: null, school_year_name: null });

  // Mount-only load; getStudentData is recreated each render and must not be a
  // dep.
  useEffect(() => {
    getStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!student_id) return;
    (async () => {
      const { data: sgl } = await getStudentGradeLevelExtras(student_id);
      if (cancelled) return;
      const top = (sgl ?? [])[0] as any;
      setExtras({
        grade_level: top?.grade_levels?.grade_level ?? null,
        // `curriculums` is a TOP-LEVEL embed off student_grade_levels.curriculum_id,
        // not nested under grade_levels — see getStudentGradeLevelExtras.
        curriculum_name: top?.curriculums?.curriculum_name ?? null,
        school_year_name: top?.school_years?.name ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [student_id]);

  return (
    <Stack sx={{ minHeight: "100vh" }} spacing={4}>
      <Stack spacing={2}>
        <PageHeader eyebrow="Teacher" title="Student Name" actions={<BackIcon />} />
        <SurfaceCard sx={{ p: 4, borderRadius: 3 }}>
          <Stack spacing={1}>
            <Grid container sx={{ alignItems: "center", flexWrap: "nowrap" }} spacing={2}>
              <Avatar
                sx={{ width: "3.2rem", height: "auto", aspectRatio: 1 }}
                alt={data?.first_name}
                // `students` has no avatar column — fall back to the initials
                // child below (src undefined renders the fallback).
                src={undefined}
              >
                {capitalize(first(data?.first_name))}
              </Avatar>

              <Grid>
                <Typography variant="subtitle2">
                  {startCase(`${data?.first_name} ${data?.last_name}`)}
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={{ xs: 0, md: 2 }} width="100%">
              <Grid size={{ xs: 12, md: 4 }}>
                <Info label="Email ID" val={data?.email} />
                <Info
                  label="Date of birth"
                  val={formatDate((data as any)?.dob ?? (data as any)?.DOB, "MMM dd, yyy")}
                />
                <Info label="Role" val={(data as any)?.base_role ?? (data as any)?.role} />
                <Info label="Status" val={startCase((data as any)?.status)} />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Info label="ID Number" val={(data as any)?.id_number} />
                <Info label="Grade Level" val={extras.grade_level ?? "-"} />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Info label="Curriculum" val={extras.curriculum_name ?? "-"} />
                <Info label="School Year" val={extras.school_year_name ?? "-"} />
              </Grid>
            </Grid>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {parents.map((p, key) => (
            <Fragment key={p.id}>
              {!!key && <Divider sx={{ my: 2 }} />}

              <Stack spacing={1}>
                <Grid>
                  <Typography variant="subtitle2">{`Parent ${key + 1} Details`}</Typography>
                </Grid>

                <Grid container>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Info label="Name" val={`${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || '-'} />
                    <Info label="Email ID" val={p.email ?? '-'} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Info label="ID Number" val={p.id_number ?? '-'} />
                    <Info label="Role" val="parent" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Info label="Contact Number" val={p.mobile_number ?? '-'} />
                  </Grid>
                </Grid>
              </Stack>
            </Fragment>
          ))}
        </SurfaceCard>
      </Stack>

      <AnalyticsTabs />

      <RecentActivities />

      <PendingLessons />
    </Stack>
  );
};

const Info = ({ label, val }: { label: string; val?: string }) => (
  <Grid container alignItems="baseline" spacing={1}>
    <Grid>
      <Typography variant="subtitle1">{label}:</Typography>
    </Grid>
    <Grid>
      <Typography variant="subtitle2">{val || "-"}</Typography>
    </Grid>
  </Grid>
);

export default StudentProfile;
